import { z } from 'zod';
import { EdgeLayoutSchema, GeneratedNodePositionSchema, ProcessLayoutScopeSchema, StepPositionSchema, SwimlaneColorSchema } from '../../entities/process-layout';
/**
 * Classify a layout node id: generated-node (`start-node`, `end-*`,
 * `gateway-*`, `assignment-*`, `event-*`) vs a real `stableStepId`. Shared
 * between frontend and backend so the editor and the layout service split
 * writes the same way.
 */ export function isGeneratedNodeId(id) {
    return id === 'start-node' || id.startsWith('end-') || id.startsWith('gateway-') || id.startsWith('assignment-') || id.startsWith('event-');
}
/**
 * End-node id: `end-<predecessorStableId>` or
 * `end-<predecessorStableId>-<branchKey>`, where the branch key is the
 * representative rule id of a decision output. Both ids are 24-hex ObjectIds,
 * so splitting on `-` is unambiguous.
 */ export const END_NODE_ID_PREFIX = 'end-';
const END_NODE_ID_RE = /^end-([a-f\d]{24})(?:-([a-f\d]{24}))?$/i;
export function buildEndNodeId(predecessorStableId, branchKey) {
    return branchKey ? `${END_NODE_ID_PREFIX}${predecessorStableId}-${branchKey}` : `${END_NODE_ID_PREFIX}${predecessorStableId}`;
}
export function parseEndNodeId(nodeId) {
    const match = END_NODE_ID_RE.exec(nodeId);
    if (!match) return null;
    return match[2] ? {
        predecessorStableId: match[1],
        branchKey: match[2]
    } : {
        predecessorStableId: match[1]
    };
}
/**
 * GET /api/v2/process/:processId/layout?scope=draft|published
 *
 * Returns the layout doc for the requested scope, or an empty shape when no
 * doc exists. An explicit `published` scope is an ordinary read; an explicit
 * `draft` scope requires `Update` on the process. An omitted scope resolves to
 * the draft layout when one exists — and then requires `Update` too — and to
 * the published layout otherwise, which any reader may fetch.
 */ export const GetProcessLayoutQuerySchema = z.object({
    scope: ProcessLayoutScopeSchema.optional()
});
export const GetProcessLayoutResponseSchema = z.object({
    scope: ProcessLayoutScopeSchema,
    stepPositions: z.array(StepPositionSchema),
    generatedNodePositions: z.array(GeneratedNodePositionSchema),
    swimlaneOrder: z.array(z.string()),
    laneColors: z.array(SwimlaneColorSchema),
    edgeLayouts: z.array(EdgeLayoutSchema),
    updatedAt: z.date().nullable()
});
/**
 * PUT /api/v2/process/:processId/layout
 *
 * Upserts the layout doc for `scope`. Only keys present in `patch` are
 * written; keys omitted from `patch` are left untouched on the stored doc.
 * `stepPositions` is a per-node upsert: each listed entry replaces the stored
 * one with the same `stableStepId` and every other stored entry is kept, so a
 * node create's server-side position write is never erased by a concurrent
 * drag save. Every other key replaces the stored array wholesale.
 */ export const UpdateProcessLayoutPatchSchema = z.object({
    stepPositions: z.array(StepPositionSchema).max(500).optional(),
    generatedNodePositions: z.array(GeneratedNodePositionSchema).max(500).optional(),
    swimlaneOrder: z.array(z.string().max(100)).max(50).optional(),
    laneColors: z.array(SwimlaneColorSchema).max(50).optional(),
    edgeLayouts: z.array(EdgeLayoutSchema).max(1000).optional()
}).refine((p)=>p.stepPositions !== undefined || p.generatedNodePositions !== undefined || p.swimlaneOrder !== undefined || p.laneColors !== undefined || p.edgeLayouts !== undefined, {
    message: 'patch must include at least one key'
});
export const UpdateProcessLayoutRequestSchema = z.object({
    scope: ProcessLayoutScopeSchema,
    patch: UpdateProcessLayoutPatchSchema
});
export const UpdateProcessLayoutResponseSchema = z.object({
    updatedAt: z.date()
});

//# sourceMappingURL=process-layout.schema.js.map